import SavedSearchIcon from '@mui/icons-material/SavedSearch';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { CopyToClipboardButton } from '../../../../components/CopyToClipboardButton';
import { CodeBlock, Field, FieldGrid, SectionCard } from '../../../../components/Section';
import { fetchStatuses } from '../../../../constants/fetchStatuses';
import { useAppDispatch } from '../../../../redux/hooks';
import { fetchAnbPaymentDetails } from '../../../../redux/modules/payment/payment.actions';
import { anbPaymentDetailsSelector, paymentSelector } from '../../../../redux/modules/payment/payment.selectors';
import { ReducerState } from '../../../../redux/store.types';
import { IAlpacaTransfer } from '../../../../types/alpaca';
import { round2Format } from '../../../../utils/number';

interface AlpacaTransferProps {
  alpacaTransfer: IAlpacaTransfer;
}

export function AlpacaTransfer({ alpacaTransfer }: AlpacaTransferProps) {
  const { t } = useTranslation('apiError');
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { fetchAnbPaymentDetailsStatus, error } = useSelector(paymentSelector);
  const paymentDetails = useSelector((state: ReducerState) =>
    anbPaymentDetailsSelector(state, alpacaTransfer.anbTransferId),
  );

  const fetchAnbTransferDetails = async () => {
    if (alpacaTransfer.anbTransferId) {
      try {
        await dispatch(fetchAnbPaymentDetails(alpacaTransfer.anbTransferId)).unwrap();

        enqueueSnackbar('Anb Payment details found successfully!', { variant: 'success' });
      } catch (err: any) {
        enqueueSnackbar(`Fetch Anb Payment details failed: ${t(err?.message)} ${JSON.stringify(err?.data)}`, {
          variant: 'error',
        });
      }
    }
  };

  const renderPaymentDetails = () => {
    if (error) {
      return (
        <Field label="Payment details" fullWidth>
          <Alert severity="error" sx={{ borderRadius: '8px' }}>
            {error.message}
          </Alert>
        </Field>
      );
    }

    return (
      <Field label="Payment details" fullWidth>
        <CodeBlock value={paymentDetails ? JSON.stringify(paymentDetails, null, 2) : 'Not found'} maxHeight={240} />
      </Field>
    );
  };

  return (
    <SectionCard title="Transfer" sx={{ mt: '16px' }}>
      <FieldGrid columns={3}>
        <Field label="Id">
          <CopyToClipboardButton text={alpacaTransfer.transferId} value={alpacaTransfer.transferId} />
        </Field>
        <Field label="NTA id">
          <CopyToClipboardButton text={alpacaTransfer.ntaId} value={alpacaTransfer.ntaId} />
        </Field>
        <Field label="Status" value={alpacaTransfer.status} />
        <Field label="FX rate" value={alpacaTransfer.fxRate} />
        <Field label="Real FX rate" value={alpacaTransfer.realFxRate} />
        <Field label="Direction" value={alpacaTransfer.direction} />
        <Field label="SAR amount" value={round2Format(alpacaTransfer.sarAmount)} />
        <Field label="Actual SAR amount" value={round2Format(alpacaTransfer.actualSarAmount)} />
        <Field label="USD amount" value={round2Format(alpacaTransfer.usdAmount)} />
        <Field label="Original USD amount" value={round2Format(alpacaTransfer.originalUsdAmount)} />
        <Field label="Actual USD amount" value={round2Format(alpacaTransfer.actualUsdAmount)} />
        <Field label="ANB transfer id">
          <LoadingButton
            type="button"
            color="primary"
            size="small"
            variant="text"
            endIcon={<SavedSearchIcon />}
            disabled={!alpacaTransfer.anbTransferId}
            loading={fetchAnbPaymentDetailsStatus === fetchStatuses.pending}
            loadingPosition="end"
            onClick={fetchAnbTransferDetails}
            sx={{ ml: '-6px' }}
          >
            {alpacaTransfer.anbTransferId || '—'}
          </LoadingButton>
        </Field>
        {renderPaymentDetails()}
      </FieldGrid>
    </SectionCard>
  );
}
